/**
 * PrLitReviewOverviewPage
 *
 * The PR Literature Review section's landing page. A non-table page: it keeps
 * the shared sidebar frame but renders the PR (Parasite Rate) literature review
 * status overview instead of an article table.
 *
 * The page reads the PR literature review collections (the cycles) from the
 * shared context, fetches each one's per-status counts (a lightweight
 * server-side aggregate via `getJournalArticleStatusCounts`), builds
 * `CollectionStatusEntry` items, and hands them to `PrLitReviewStatusChart`
 * which renders the legend, bars, and hover tooltip.
 */

import { useEffect, useMemo, useState } from "react"
import PrLiteratureReviewShell from "@/components/pr-literature-review/PrLiteratureReviewShell"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { useAuth } from "@/hooks/useAuth"
import { usePrLiteratureReview } from "@/components/pr-literature-review/PrLiteratureReviewContext"
import { BiblioCollection, PR_LITERATURE_REVIEW_TYPE } from "@/services/biblioService"
import {
  JournalArticleStatusCountsResponse,
  getJournalArticleStatusCounts,
} from "@/services/journalArticlesService"
import PrLitReviewStatusChart, {
  CollectionStatusEntry,
} from "@/components/journal-articles/PrLitReviewStatusChart"

/** Browser tab title and page heading. */
const PAGE_TITLE: string = "Overview"

/** The API topic slug for PR (parasite rate) articles. */
const PR_TOPIC_SLUG: string = "pr"

/** Short description shown under the page heading. */
const PAGE_DESCRIPTION: string = "Status breakdown of each PR literature review GBD cycle."

/**
 * The fetched per-status counts for each collection, keyed by collection id.
 */
type CountsByCollectionId = Record<number, JournalArticleStatusCountsResponse>

/**
 * The PR Lit Review Overview page: fetches per-cycle status counts and renders
 * the status chart inside the shared sidebar frame.
 *
 * @returns The rendered page element.
 */
export default function PrLitReviewOverviewPage(): React.JSX.Element {
  const { getAccessToken } = useAuth()
  const { collections, collectionsLoading } = usePrLiteratureReview()

  // Per-collection status counts, and whether that fetch is in flight.
  const [countsByCollectionId, setCountsByCollectionId] = useState<CountsByCollectionId>({})
  const [countsLoading, setCountsLoading] = useState<boolean>(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // The PR literature review collections (cycles), sorted by name in reverse
  // alphabetical (Z→A) order for a stable layout.
  const prCollections: BiblioCollection[] = useMemo(
    () =>
      collections
        .filter((collection) => collection.collectionType === PR_LITERATURE_REVIEW_TYPE)
        .sort((a, b) => b.collectionName.localeCompare(a.collectionName)),
    [collections]
  )

  // Once collections have loaded, fetch every PR cycle's status counts in
  // parallel and store them keyed by collection id.
  useEffect(() => {
    if (collectionsLoading) {
      return
    }

    if (prCollections.length === 0) {
      setCountsByCollectionId({})
      setCountsLoading(false)
      return
    }

    let cancelled: boolean = false

    /**
     * Fetches per-status counts for every PR cycle and stores the results.
     * Guards against state updates after unmount via the `cancelled` flag.
     */
    async function loadCounts(): Promise<void> {
      setCountsLoading(true)
      setLoadError(null)

      try {
        const token: string = await getAccessToken()

        const results: JournalArticleStatusCountsResponse[] = await Promise.all(
          prCollections.map((collection) =>
            getJournalArticleStatusCounts(token, collection.id, PR_TOPIC_SLUG)
          )
        )

        if (cancelled) {
          return
        }

        const next: CountsByCollectionId = {}
        for (const result of results) {
          next[result.collectionId] = result
        }
        setCountsByCollectionId(next)
      } catch (err) {
        if (cancelled) {
          return
        }
        setLoadError(
          err instanceof Error ? err.message : "Failed to load PR literature review summaries"
        )
      } finally {
        if (!cancelled) {
          setCountsLoading(false)
        }
      }
    }

    loadCounts()

    return () => {
      cancelled = true
    }
  }, [collectionsLoading, prCollections, getAccessToken])

  // Build the entries the chart component expects.
  const chartEntries: CollectionStatusEntry[] = useMemo(
    () =>
      prCollections.map((collection) => ({
        collectionId: collection.id,
        collectionName: collection.collectionName,
        statusCounts: countsByCollectionId[collection.id],
      })),
    [prCollections, countsByCollectionId]
  )

  const isLoading: boolean = collectionsLoading || countsLoading

  return (
    <PrLiteratureReviewShell title={PAGE_TITLE} description={PAGE_DESCRIPTION}>
      <div className="flex flex-1 flex-col min-h-0 overflow-auto">
        {isLoading ? (
          <LoadingSpinner variant="outline" loadingContent="Loading PR lit review summaries..." />
        ) : loadError !== null ? (
          <p className="text-sm text-destructive">{loadError}</p>
        ) : prCollections.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No PR literature review cycles found.
          </p>
        ) : (
          <PrLitReviewStatusChart entries={chartEntries} />
        )}
      </div>
    </PrLiteratureReviewShell>
  )
}
